import { Link } from '@/components/Link';
import { DashboardHeader } from '../../components/DashboardHeader';
import { DashboardSection } from '../../components/DashboardSection';
import { StatRow } from '../../components/StatRow';
import { useCatalogLinks } from '../../components/CatalogLinks';
import type { ApiDetail, Endpoint } from '../../features/catalog/model';
import { cheapest } from '../../lib/catalog';
import { fmtUtcDay, usdCost } from '../../lib/format';
import { quotedRateLabel, quotedRateUsd } from '../../lib/pricing';
import { m } from '../../paraglide/messages';
import { ProvidersPanel } from './ProvidersPanel';
import { PublicTryConsole } from './PublicTryConsole';
import { EndpointReferenceContent } from './EndpointReferenceContent';
import { REFERENCE_ID } from './EndpointReference';

export interface DashboardEndpointDetailBodyProps {
  api: ApiDetail;
  endpoint: Endpoint;
  /** RFC3339 instant the gateway last recomputed the health rollup */
  verifiedAt?: string;
  /** the SKU's request JSON Schema, when the gateway published one */
  inputSchema?: unknown;
  /** the SKU's response JSON Schema, when the gateway published one */
  outputSchema?: unknown;
}

/**
 * An endpoint inside the dashboard: what it costs, who serves it, a free sample,
 * and the request contract. No editorial bands, FAQ, sibling shelf or funnel
 * banner.
 *
 * The sample is the same house-funded `/public/try` execution the public page
 * runs, deliberately: it is free, capped, and NOT a paid run against the
 * customer's wallet. The paid run is the Playground, which the sample section
 * links to rather than imitates.
 */
export const DashboardEndpointDetailBody = ({
  api,
  endpoint,
  verifiedAt,
  inputSchema,
  outputSchema,
}: DashboardEndpointDetailBodyProps) => {
  const links = useCatalogLinks();
  const cheap = cheapest(endpoint);
  const day = fmtUtcDay(verifiedAt);
  return (
    <div className="flex flex-col gap-8">
      <DashboardHeader
        crumb={
          <Link to={links.api(api.id)} className="hover:text-text">
            {api.name}
          </Link>
        }
        title={endpoint.action}
        description={
          <span className="mono text-13">
            {endpoint.method} {endpoint.path}
          </span>
        }
      />

      <StatRow
        stats={[
          {
            k: endpoint.providers.length === 1 ? m.endpoint_spec_price() : m.app_endpoint_cheapest_price(),
            v: usdCost(quotedRateUsd(cheap.price)),
            unit: quotedRateLabel(cheap.price),
          },
          { k: m.app_endpoint_source(), v: <span className="text-16">{cheap.source.name}</span> },
          {
            k: m.app_endpoint_last_verified(),
            v: <span className="text-16">{day || m.app_endpoint_not_measured()}</span>,
          },
        ]}
      />

      <DashboardSection
        title={m.platform_header_providers()}
        description={m.app_endpoint_providers_note()}
      >
        <ProvidersPanel
            key={endpoint.id}
            providers={endpoint.providers}
            notServing={endpoint.notServing}
          />
      </DashboardSection>

      <DashboardSection
        title={m.app_endpoint_free_sample()}
        description={m.app_endpoint_free_sample_note()}
        action={
          <Link to="/dashboard/playground" className="text-13 text-accent">
            {m.app_endpoint_run_in_playground()}
          </Link>
        }
      >
        <PublicTryConsole
          key={`${endpoint.id}:${endpoint.sku}`}
          endpoint={endpoint}
          title={`${api.name} ${endpoint.action}`}
          acquisition={false}
        />
      </DashboardSection>

      <DashboardSection id={REFERENCE_ID} title={m.app_endpoint_reference_heading()}>
        <EndpointReferenceContent
          endpoint={endpoint}
          inputSchema={inputSchema}
          outputSchema={outputSchema}
        />
      </DashboardSection>
    </div>
  );
};
